<div class="avatar-box-layout"
     [class.minimized]="isMinimizedMode$ | async"
     [class.fullscreen]="!(isMinimizedMode$ | async)">
     
  @if (avatar$ | async; as avatar) {
    @if (conversationState$ | async; as state) {
      
      <div class="layout-grid" [class.camera-on]="isCameraPreviewVisible(state)">
        
        <!-- AVATAR CARD (White box in fullscreen, or entire circle in minimized) -->
        <div class="avatar-card" 
             [class.session-active]="isSessionActive$ | async"
             [class.session-inactive]="isSessionInactive$ | async"
             [class.visual-session-active]="state.isVisualSessionOn"
             [class.visual-session-inactive]="!state.isVisualSessionOn">
          
          <!-- Background Video (idle state looping face) & Avatar Loading -->
          @if (shouldShowBackground(avatar) && !state.isVisualSessionOn) {
            <div class="avatar-circle-wrapper" 
                 [class.loading]="isBackgroundVideoLoading()"
                 [class.session-loading]="state.isLoading"
                 [class.visual-session-loading]="state.isVisualSessionStarting">
              @if (isBackgroundVideoLoading() && !state.isLoading) {
                <div class="avatar-loading-overlay video-loading">
                  <app-loading-container 
                    [fullscreen]="false"
                    [background]="false"
                    [backdropBlur]="true"
                    [scale]="0.5">
                  </app-loading-container>
                </div>
              } @else if (state.isLoading) {
                <div class="avatar-loading-overlay session-loading-overlay">
                  <div class="logo-loader-container">
                    <img src="img/greatday_logo_icon.svg" alt="Loading..." class="logo-loader-img">
                  </div>
                </div>
              }
              
              <video 
                     #backgroundVideo
                     class="background-video"
                     [class.video-hidden]="isBackgroundVideoLoading()"
                     [src]="'https://storage.googleapis.com/simliai2.appspot.com/videos/' + avatar.configuration?.['face_id'] + '.mp4'"
                     [style.will-change]="'transform'"
                     (canplay)="onBackgroundVideoLoaded()"
                     (loadeddata)="onBackgroundVideoLoaded()"
                     (error)="onBackgroundVideoError($event)"
                     loading="lazy" 
                     autoplay 
                     loop 
                     muted
                     playsinline
                     webkit-playsinline
                     disablepictureinpicture
                     controlslist="nodownload noplaybackrate">
              </video>
            </div>
          }
          
          <!-- Streaming Video Element (Simli WebRTC) Wrapper to isolate layout context in Safari -->
          <div class="video-wrapper"
               [style.opacity]="(state.isVisualSessionOn) ? 1 : 0"
               [style.pointer-events]="(state.isVisualSessionOn) ? 'auto' : 'none'">
            <video class="video-element" 
                   #videoElement 
                   autoplay 
                   playsinline
                   webkit-playsinline
                   muted
                   disablepictureinpicture
                   controlslist="nodownload noplaybackrate"
                   poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=">
            </video>
          </div>
          
          <!-- Loading overlay (Minimized mode only) -->
          @if (state.isLoading && (isMinimizedMode$ | async)) {
            <div class="minimized-loading-overlay">
              <app-loading-container 
                [fullscreen]="false"
                [background]="true"
                [backdropBlur]="true"
                [scale]="0.5">
              </app-loading-container>
            </div>
          }

          <!-- Fullscreen-only inside-card controls -->
          @if (!(isMinimizedMode$ | async)) {
            @if (state.isStreaming) {
              <div class="card-top-controls">
                <!-- Top-Left: Camera status/toggle button -->
                <button class="camera-status-btn"
                        [class.active]="state.isVisualSessionOn"
                        [disabled]="state.isVisualSessionStarting || state.isVisualSessionStopping"
                        (click)="state.isVisualSessionOn ? stopVisualSession() : startVisualSession()"
                        [attr.aria-label]="state.isVisualSessionOn ? ('avatarBox.cameraOff' | translate) : ('avatarBox.cameraOn' | translate)">
                  @if (state.isVisualSessionStarting || state.isVisualSessionStopping) {
                    <iconify-icon icon="svg-spinners:180-ring" class="refresh-icon"></iconify-icon>
                  } @else {
                    <iconify-icon [icon]="state.isVisualSessionOn ? 'heroicons-solid:video-camera' : 'heroicons-solid:video-camera-slash'"></iconify-icon>
                  }
                </button>
                
                <!-- Top-Right: Subtitles (CC) button -->
                <button class="cc-status-btn"
                        [class.active]="subtitlesEnabled$ | async"
                        (click)="onToggleSubtitles()"
                        [attr.aria-label]="'Toggle Subtitles'">
                  <span class="cc-badge">CC</span>
                </button>
              </div>
            }

            <!-- Low minutes toast (Centered in card top) -->
            <div class="toast-wrapper">
              <app-low-minutes-toast></app-low-minutes-toast>
            </div>
            
            <!-- Interpreter Mode Badge -->
            @if (interpreterModeEnabled) {
              @if (interpreterMode$ | async; as iMode) {
                @if (iMode.enabled && iMode.userLanguage && iMode.targetLanguage) {
                  <div class="interpreter-controls-container" [class.subtitles-visible]="(subtitlesEnabled$ | async) ?? false">
                    <div class="interpreter-mode-badge">
                      <button class="language-indicator-button">
                        <span class="lang-code">{{ getLanguageCode(iMode.userLanguage) }}</span>
                        <iconify-icon icon="heroicons-solid:arrows-right-left" class="swap-icon"></iconify-icon>
                        <span class="lang-code">{{ getLanguageCode(iMode.targetLanguage) }}</span>
                      </button>
                    </div>
                    <button class="end-translation-btn" (click)="stopInterpreterMode()">
                      <span>{{ 'avatarBox.endTranslation' | translate }}</span>
                    </button>
                  </div>
                }
              }
            }
            
            <!-- Subtitles/Speech bubble at the bottom of the card -->
            @if (state.isStreaming && (subtitlesEnabled$ | async)) {
              <div class="avatar-subtitle-overlay">
                <app-subtitle></app-subtitle>
              </div>
            }
          }
          
        </div> <!-- /avatar-card -->
        
        <!-- USER CAMERA CARD (Always in DOM for findVideoElements, hidden via CSS if not visible or minimized) -->
        <div class="user-camera-card" 
             [class.visible]="isCameraPreviewVisible(state) && !(isMinimizedMode$ | async)"
             [class.loading]="isCameraPreviewLoading(state)">
          <!-- Local Camera Preview Wrapper to isolate layout context in Safari -->
          <div class="camera-wrapper">
            <video class="local-camera-preview"
                   #localCameraPreview
                   autoplay
                   playsinline
                   webkit-playsinline
                   muted
                   disablepictureinpicture
                   controlslist="nodownload noplaybackrate">
            </video>
          </div>
          
          @if (isCameraPreviewLoading(state)) {
            <div class="camera-preview-loading">
              <app-loading-container
                [fullscreen]="false"
                [background]="false"
                [backdropBlur]="false"
                [scale]="0.36">
              </app-loading-container>
            </div>
          }
          
          <!-- Camera Card Controls Overlay -->
          @if (!(isMinimizedMode$ | async) && !state.isLoading) {
            <div class="camera-card-overlays">
              <!-- Swap Camera Button (Top-left of camera feed) -->
              @if (state.canSwitchCamera) {
                <button class="swap-camera-btn"
                        [disabled]="state.cameraPreviewStatus !== 'on' || state.isCameraSwitching || state.isVisualSessionStarting || state.isVisualSessionStopping"
                        (click)="switchCamera()"
                        [attr.aria-label]="'Switch Camera'">
                  @if (state.isCameraSwitching) {
                    <iconify-icon icon="svg-spinners:180-ring" class="refresh-icon"></iconify-icon>
                  } @else {
                    <iconify-icon icon="heroicons-solid:arrow-path"></iconify-icon>
                  }
                </button>
              }
              
              <!-- Floating controls bar inside camera feed -->
              <div class="camera-controls-bar">
                <button class="control-btn mic-btn" [class.muted]="state.isMicrophoneMuted" (click)="toggleMicrophoneMute()">
                  <iconify-icon [icon]="state.isMicrophoneMuted ? 'heroicons-solid:microphone-slash' : 'heroicons-solid:microphone'"></iconify-icon>
                </button>
                
                <button class="control-btn camera-btn" (click)="stopUserCamera()">
                  <iconify-icon icon="heroicons-solid:video-camera"></iconify-icon>
                </button>
                
                <button class="control-btn hangup-btn" (click)="stop()">
                  <iconify-icon icon="heroicons-solid:phone-x-mark"></iconify-icon>
                </button>
                
                <button class="control-btn feedback-btn" (click)="openFeedbackDialog()">
                  <iconify-icon icon="heroicons-solid:chat-bubble-oval-left-ellipsis"></iconify-icon>
                </button>
              </div>
            </div>
          }
        </div>
        
        <!-- SCREEN BOTTOM CONTROLS CAPSULE (Fullscreen only, visible when camera is OFF and streaming or loading) -->
        @if (!(isMinimizedMode$ | async) && !isCameraPreviewVisible(state) && (state.isStreaming || state.isLoading)) {
          <div class="bottom-controls-wrapper" [class.loading-state]="state.isLoading">
            <div class="bottom-controls-capsule">
              <div class="controls-group left-group">
                <button class="control-btn mic-btn" 
                        [class.muted]="state.isMicrophoneMuted" 
                        [disabled]="state.isLoading"
                        (click)="toggleMicrophoneMute()">
                  <iconify-icon [icon]="state.isMicrophoneMuted ? 'heroicons-solid:microphone-slash' : 'heroicons-solid:microphone'"></iconify-icon>
                </button>
                
                <button class="control-btn camera-btn muted" 
                        [disabled]="state.isLoading"
                        (click)="startUserCamera()">
                  <iconify-icon icon="heroicons-solid:video-camera-slash"></iconify-icon>
                </button>
              </div>
              <div class="controls-group right-group">
                <button class="control-btn hangup-btn" 
                        [disabled]="state.isLoading"
                        (click)="stop()">
                  <iconify-icon icon="heroicons-solid:phone-x-mark"></iconify-icon>
                </button>
                
                <button class="control-btn feedback-btn" 
                        [disabled]="state.isLoading"
                        (click)="openFeedbackDialog()">
                  <iconify-icon icon="heroicons-solid:chat-bubble-oval-left-ellipsis"></iconify-icon>
                </button>
              </div>
            </div>
          </div>
        }
        
        <!-- START CHAT BUTTON (Fullscreen only, visible when NOT streaming) -->
        @if (!(isMinimizedMode$ | async) && !state.isStreaming && !state.isLoading) {
          <div class="start-conversation-wrapper">
            <button class="start-chat-btn" (click)="start()">
              <span>{{ 'avatarBox.startChat' | translate }}</span>
            </button>
          </div>
        }


        
      </div> <!-- /layout-grid -->
      
      <!-- Audio output element (must remain in DOM to play stream audio) -->
      <audio #audioElement autoplay></audio>
      
    }
  } @else {
    <!-- No avatar selected state -->
    <div class="no-avatar-state">
      <button mat-flat-button class="select-avatar-btn" (click)="navigateToAvatars($event)">
        <span>{{ 'avatarBox.selectAvatar' | translate }}</span>
      </button>
    </div>
  }
</div>
